import moment from "moment";
import _ from "lodash";

/**
 * Calculates the standard deviation value from the given array of numbers
 * @param {Array<Number>} numbers - array of numbers
 * @returns {number} number
 */
export const standardDeviation = numbers => {
  const mean = numbers.reduce((sum, current) => sum + current) / numbers.length;
  const squareDiffs = numbers.map(value => {
    const diff = value - mean;
    return diff * diff;
  });
  const variance =
    squareDiffs.reduce((sum, current) => sum + current) / squareDiffs.length;
  const devationValue = Math.sqrt(variance);
  return {
    deviationValue: devationValue,
    deviationPercentage: (devationValue / mean) * 100
  };
};

/**
 * Normalizes a string by converting it to lowercase and removing whitespace and special characters.
 * @param {string} s - The input string.
 * @returns {string} - The normalized string.
 */
export const normalizeKey = (s) =>
    String(s)
      .toLowerCase()
      .replace(/[\s_\-]/g, "");

export const isObj = (v) => v && typeof v === 'object' && !Array.isArray(v);

/**
 * Checks the date is greater than today or not
 * @param {Date} date
 * @returns {boolean}
 */
export const checkDateisAfterToday = date => {
  return moment(date).isAfter();
};

/**
 * Gives the days ago value
 * @param {Date} date - date
 * @returns {string}
 */
export const getDaysAgoString = date => {
  let currentDate = moment.now();
  let dateInDB = moment(date);
  let result = dateInDB.diff(currentDate, "days");
  if (result < 0) {
    return `${-result} days ago`;
  } else {
    return `${result} days ago`;
  }
};

/**
 * Gives the days ago value
 * @param {Date} date - date
 * @returns {number}
 */
export const getDaysAgo = date => {
  let currentDate = moment.now();
  let dateInDB = moment(date);
  let result = dateInDB.diff(currentDate, "days");
  if (result < 0) {
    return -result;
  } else {
    return result;
  }
};

/**
 * Return the color based on date with current date
 * @param {Date} date - date
 * @returns {string}
 */
export const checkDateForColorView = date => {
  if (date) {
    let currentDate = moment();
    let checkingDate = moment(date);
    let result = currentDate.diff(checkingDate, "days");
    if (result >= 7) {
      return "red";
    } else if (result <= 6 && result >= 3) {
      return "purple";
    } else if (result >= 1 && result <= 2) {
      return "orange";
    } else {
      return "green";
    }
  } else {
    return "black";
  }
};

/**
 * converts the value in pixel to mm
 * @param {number} valueInPixel
 * @returns {number}
 */
export const convertTomm = valueInPixel => {
  return valueInPixel / 6.25;
};

/**
 * Return the value fixed to 2 digits after point
 * @param {*} data
 * @returns {number}
 */
export const roundOffTwo = data => {
  return +(Math.round(data + "e+" + 2) + "e-" + 2);
};

/**
 * Return the value fixed to 1 digit after point
 * @param {*} data
 * @returns {number}
 */
export const roundOff = data => {
  return +(Math.round(data + "e+" + 1) + "e-" + 1);
};

/**
 * Compares the two given objects and return boolean value
 * @param {object} object1
 * @param {object} object2
 * @returns {boolean} boolean
 */
export const compareObjects = (object1, object2) => {
  return _.isEqual(object1, object2);
};

export const isNotExists = value => [null, undefined].includes(value);

export const detectChanges = (userData, remoteConfigData) => {
  let ChangesMade = false;
  if (remoteConfigData) {
    let DefaultCalibration = JSON.parse(
      remoteConfigData.DefaultCalibration._value
    );
    if (
      userData.ReadCalibrationFromBackend &&
      !userData.ReadCalibrationFromBackend
    ) {
      ChangesMade = true;
    }
    if (
      userData.DefaultCalibrationMoment &&
      userData.DefaultCalibrationMoment !==
        Number(remoteConfigData.DefaultCalibrationMoment._value)
    ) {
      ChangesMade = true;
    }
    if (
      userData.MovementSpeed &&
      userData.MovementSpeed !==
        Number(remoteConfigData.DefaultUserSpeed._value)
    ) {
      ChangesMade = true;
    }
    if (
      userData.PersonalOffset &&
      userData.PersonalOffset !==
        Number(remoteConfigData.DefaultPersonalOffset._value)
    ) {
      ChangesMade = true;
    }
    if (
      userData.RecommendedSessionsPerWeek &&
      userData.RecommendedSessionsPerWeek !==
        Number(remoteConfigData.RecommendedSessionsPerWeek._value)
    ) {
      ChangesMade = true;
    }
    if (userData.CalibrationBackend) {
      if (
        userData.CalibrationBackend.offSetValue &&
        userData.CalibrationBackend.offSetValue !==
          Number(DefaultCalibration.offSetValue)
      ) {
        ChangesMade = true;
      }
      if (
        userData.CalibrationBackend.pendlumLength &&
        userData.CalibrationBackend.pendlumLength !==
          Number(DefaultCalibration.pendlumLength)
      ) {
        ChangesMade = true;
      }
      if (userData.CalibrationBackend.deviceDimensions) {
        if (
          userData.CalibrationBackend.deviceDimensions.width &&
          userData.CalibrationBackend.deviceDimensions.width !==
            Number(DefaultCalibration.deviceDimensions.width)
        ) {
          ChangesMade = true;
        }
        if (
          userData.CalibrationBackend.deviceDimensions.height &&
          userData.CalibrationBackend.deviceDimensions.height !==
            Number(DefaultCalibration.deviceDimensions.height)
        ) {
          ChangesMade = true;
        }
        if (
          userData.CalibrationBackend.deviceDimensions.pixelRatio &&
          userData.CalibrationBackend.deviceDimensions.pixelRatio !==
            Number(DefaultCalibration.deviceDimensions.pixelRatio)
        ) {
          ChangesMade = true;
        }
      }
    }
    return ChangesMade;
  } else {
    return ChangesMade;
  }
};
export const nthCalc = n => {
  return ["st", "nd", "rd"][((((n + 90) % 100) - 10) % 10) - 1] || "th";
};
export const nth = n => {
  return n + ["st", "nd", "rd"][((((n + 90) % 100) - 10) % 10) - 1] || n + "th";
};

export function uuidv4() {
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
    (
      (c ^ crypto.getRandomValues(new Uint8Array(1))[0]) &
      (15 >> (c / 4))
    ).toString(16)
  );
}
